import type React from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { UiThemeProvider, type ThemeMode } from '@pitbrain/ui'
import { pitbrainDarkTheme, pitbrainLightTheme } from './theme'

const STORAGE_KEY = 'pitbrain-color-mode'

interface ColorModeContextValue {
  mode: ThemeMode
  setMode: (mode: ThemeMode) => void
  toggleMode: () => void
}

const ColorModeContext = createContext<ColorModeContextValue | undefined>(
  undefined
)

/**
 * Initial mode: a persisted user choice wins; otherwise follow the OS
 * preference; otherwise dark (the primary product experience).
 */
function resolveInitialMode(): ThemeMode {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored === 'dark' || stored === 'light') return stored
  } catch {
    // Storage unavailable (private mode / embedded) — fall through.
  }
  if (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: light)').matches
  ) {
    return 'light'
  }
  return 'dark'
}

export const ColorModeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [mode, setModeState] = useState<ThemeMode>(resolveInitialMode)

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next)
    try {
      window.localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Persisting is best-effort.
    }
  }, [])

  const toggleMode = useCallback(() => {
    setModeState((current) => {
      const next = current === 'dark' ? 'light' : 'dark'
      try {
        window.localStorage.setItem(STORAGE_KEY, next)
      } catch {
        // Persisting is best-effort.
      }
      return next
    })
  }, [])

  const value = useMemo(
    () => ({ mode, setMode, toggleMode }),
    [mode, setMode, toggleMode]
  )

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider
        theme={mode === 'dark' ? pitbrainDarkTheme : pitbrainLightTheme}
      >
        {/* Shared @pitbrain/ui components follow the same mode as MUI. */}
        <UiThemeProvider mode={mode}>
          <CssBaseline />
          {children}
        </UiThemeProvider>
      </ThemeProvider>
    </ColorModeContext.Provider>
  )
}

export function useColorMode(): ColorModeContextValue {
  const context = useContext(ColorModeContext)
  if (!context) {
    throw new Error('useColorMode must be used within a ColorModeProvider')
  }
  return context
}
